import { Loader2 } from "lucide-react";
import { useRef, useState } from "react";

import { Tip } from "@/components/ui/overlays";
import { cn } from "@/lib/utils";

/**
 * The one thin document-title bar that sits directly under the Dockview tab
 * strip on EVERY document surface (markdown, canvas, PDF, website, text). The
 * title is centred, small, and muted — the note's own big `# H1` lives in the
 * body below, not here — and is inline-editable: click to edit, Enter or blur
 * SAVES, Escape cancels. The field is NEVER disabled while saving (a disabled
 * focused input force-blurs and can race its own commit — see MarkdownEditor);
 * the spinner is the only pending affordance.
 *
 * Borderless by design (the redesign's "one borderless surface" rule): no
 * divider under it, no box around it. The left side is deliberately empty —
 * there are no back/forward controls — so the title reads dead-centre.
 *
 * The non-editing title renders inside an `<h1>` so it stays reachable as a
 * heading (assistive tech + the reader e2e that assert the document title). The
 * button carries no aria-label so its (and the heading's) accessible name is the
 * title text itself; the rename hint rides a tooltip instead.
 */
export function DocTitleBar({
  title,
  onRename,
  busy = false,
  editable = true,
  renameHint = "Rename",
}: {
  /** The current name shown centred in the bar (the file/document name). */
  title: string;
  /** Commit a non-empty, changed name. Wired to the surface's rename mutation. */
  onRename: (next: string) => void;
  /** A rename is in flight — show the inline spinner (never disable the field). */
  busy?: boolean;
  /** Non-editable surfaces (should be rare) render the title as static text. */
  editable?: boolean;
  /** Tooltip shown on hover over the title (e.g. "Rename document"). */
  renameHint?: string;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  // Escape sets this so the blur that removing the input from the DOM triggers
  // doesn't re-commit the edit Escape just cancelled (mirrors MarkdownEditor).
  const suppressBlur = useRef(false);

  const commit = () => {
    if (suppressBlur.current) {
      suppressBlur.current = false;
      return;
    }
    const value = (editing ?? "").trim();
    setEditing(null);
    if (!value || value === title) return;
    onRename(value);
  };

  return (
    <div className="flex h-9 shrink-0 items-center justify-center px-16">
      {editable && editing !== null ? (
        <div className="flex min-w-0 items-center gap-1.5">
          <input
            autoFocus
            value={editing}
            onChange={(e) => setEditing(e.target.value)}
            onFocus={(e) => e.currentTarget.select()}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              }
              if (e.key === "Escape") {
                e.preventDefault();
                suppressBlur.current = true;
                setEditing(null);
              }
            }}
            aria-label="Document title"
            aria-busy={busy}
            className="w-[min(56ch,70vw)] max-w-full bg-transparent text-center text-[13px] font-medium text-foreground outline-none"
          />
          {busy && <Loader2 className="h-3 w-3 shrink-0 animate-spin text-primary" aria-hidden />}
        </div>
      ) : editable ? (
        <h1 className="m-0 min-w-0 max-w-full text-[13px] font-medium leading-none">
          <Tip label={renameHint} side="bottom">
            <button
              type="button"
              onClick={() => {
                suppressBlur.current = false;
                setEditing(title);
              }}
              className={cn(
                "block max-w-full truncate rounded px-2 py-0.5 text-center text-[13px] font-medium",
                "text-muted-foreground transition-colors hover:text-foreground",
              )}
            >
              {title}
            </button>
          </Tip>
        </h1>
      ) : (
        <h1 className="m-0 max-w-full truncate px-2 text-center text-[13px] font-medium text-muted-foreground">
          {title}
        </h1>
      )}
    </div>
  );
}
